import React, { useContext, useEffect, useRef } from "react";
import { DataGrid, GridColumnMenu, GridFilterPanel, Toolbar, useGridApiContext } from "@mui/x-data-grid";
import { itIT } from "@mui/x-data-grid/locales";
import { Box, Button, IconButton, ListItemIcon, ListItemText, MenuItem, Tooltip, Typography } from "@mui/material";
import PushPinIcon from "@mui/icons-material/PushPin";
import { formatLabel, isComponentVisible } from "../utils/SaitUtils";
import SaitButton from "./SaitButton";
import { useSelector } from "react-redux";
import SaitButtonUpload from "./SaitButtonUpload";
import { BUTTON_UPLOAD, BUTTON_LABEL } from "../utils/ButtonTypes";
import { ModuleContext, saitfecoreNs } from "../utils/Constants";
import { checkIsRoleAllowed } from "../utils/SecurityUtils";
import i18next from "../localization/i18n";
import GridToolbarExcelButton from "../toolbar/GridToolbarExcelButton";
import GridToolbarExcelPivotButton from "../toolbar/GridToolbarExcelPivotButton";
import GridToolbarFiltersButton from "../toolbar/GridToolbarFiltersButton";
import styled from "@emotion/styled";
import GridToolbarColumnsButton from "../toolbar/GridToolbarColumnsButton";
import GridToolbarDensitySelector from "../toolbar/GridToolbarDensitySelector";
import { useTheme, alpha, darken, lighten } from "@mui/material/styles";
import { GridToolbarAutoreloadDialog, GridToolbarAutoreloadButton } from "../toolbar/GridToolbarAutoreloadButton";
import GridToolbarReloadButton from "../toolbar/GridToolbarReloadButton";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";

export default function SaitGrid(props) {
	/////////////// DEFAULT PARAMETERS ///////////////
	// MANDATORY:
	//     rows:            Righe da visualizzare in griglia
	//     columns:         Colonne della griglia
	//     isFiltering:     Parametro che maschera la griglia quando sta filtrando
	//     pageSize:        Numero di righe per pagina
	const defaultMinHeight = 200; // Altezza minima griglia
	//////////////////////////////////////////////////
	const gridRef = useRef(null);
	const draggedColumnField = useRef(null);
	const dragOverColumnField = useRef(null);
	const theme = useTheme();

	const componentModule = useContext(ModuleContext);
	const userPreferences = useSelector((state) => state["auth"]);
	const objModArray = userPreferences["objMod"];

	const [selectionModel, setSelectionModel] = React.useState({
		type: "include",
		ids: new Set([]),
	});
	const [columnVisibilityModel, setColumnVisibilityModel] = React.useState();

	const [paginationModel, setPaginationModel] = React.useState({
		pageSize: props.pageSize,
		page: props.paginationMode !== "client" ? props.page : 0,
	});

	const [columnsGrid, setColumnsGrid] = React.useState([]);

	const [visualizeGrid, setVisualizeGrid] = React.useState(true);

	/////////////////////////////////// Per autoreload ///////////////////////////////////
	const [autoreloadOn, setAutoreloadOn] = React.useState(false);
	const [openDialogAutoreload, setOpenDialogAutoreload] = React.useState(null);
	//////////////////////////////////////////////////////////////////////////////////////

	let activeTabId = useSelector((state) => state["generalConfig"]["activeTabId"]);

	const gridId = props.gridId || props.findFunction ? "GRID_" + (props.gridId || componentModule + props.findFunction) : null;

	//SETTINGS

	const settingsStorageKey = "GRID_" + (props.gridId || componentModule) + "_SETTINGS";

	const SETTINGS_DEFAULT = {
		density: "standard",
		showCellBorders: false,
		showColumnBorders: false,
	};

	const getInitialSettings = () => {
		try {
			const storedSettings = localStorage.getItem(settingsStorageKey);
			return storedSettings ? JSON.parse(storedSettings) : SETTINGS_DEFAULT;
		} catch (error) {
			return SETTINGS_DEFAULT;
		}
	};
	const [settings, setSettings] = React.useState(getInitialSettings());

	//USE-EFFECT

	useEffect(() => {
		if (componentModule === activeTabId || !activeTabId) {
			setVisualizeGrid(true);
		} else {
			setVisualizeGrid(false);
		}
	}, [activeTabId]);

	useEffect(() => {
		const savedColumns = props.gridId || props.findFunction ? getSavedColumns() : null;

		let newColumnsFind = false;

		const newColumns = props.columns.map((column) => {
			const col = { ...column };
			if (props.gridId || props.findFunction) {
				const matchingSavedColumn = savedColumns.find((savedColumn) => savedColumn.field === col.field);

				if (matchingSavedColumn) {
					col.hidden = matchingSavedColumn?.hidden;

					if (matchingSavedColumn?.width) {
						col.width = matchingSavedColumn.width;
						col.flex = undefined;
					}

					if (matchingSavedColumn.startPinned !== undefined) {
						col.startPinned = matchingSavedColumn.startPinned;
					}
					if (matchingSavedColumn.endPinned !== undefined) {
						col.endPinned = matchingSavedColumn.endPinned;
					}
				} else {
					const columnToAdd = {
						field: col.field,
						width: col.width,
						hidden: col.hidden,
						startPinned: col.startPinned,
						endPinned: col.endPinned,
					};

					savedColumns.push(columnToAdd);

					newColumnsFind = true;
				}
			}

			// Se la colonna è pinnata, non può avere flex (altrimenti occupa lo spazio residuo e sfalsa le coordinate sticky)
			if ((col.startPinned || col.endPinned) && col.flex && !col.width) {
				col.width = col.minWidth || 150;
				col.flex = undefined;
			}

			return manageColumn(col);
		});

		if (newColumnsFind && (props.gridId || props.findFunction)) {
			if (!window["Configs"].configTest && !props.disableSaveColumns) {
				localStorage.setItem(gridId, JSON.stringify(savedColumns));
			}
		}

		setColumnVisibilityModel(
			newColumns.reduce((acc, item) => {
				acc[item.field] = !item.hidden;
				return acc;
			}, {}),
		);

		// Applico l'ordine delle colonne eventualmente salvato (drag&drop)
		const orderedColumns =
			props.gridId || props.findFunction
				? [...newColumns].sort((colA, colB) => {
						const orderA = savedColumns.find((savedColumn) => savedColumn.field === colA.field)?.order;
						const orderB = savedColumns.find((savedColumn) => savedColumn.field === colB.field)?.order;
						if (orderA === undefined && orderB === undefined) {
							return 0;
						}
						if (orderA === undefined) {
							return 1;
						}
						if (orderB === undefined) {
							return -1;
						}
						return orderA - orderB;
					})
				: newColumns;

		setColumnsGrid(reorderPinnedColumns(orderedColumns));
	}, [props.columns]);

	useEffect(() => {
		if (props.selectFirstRow) {
			if (props.rows?.length > 0) {
				cambiaSelezione({
					type: "include",
					ids: new Set([props.rows[0].id]),
				});
			}
		}
		// eslint-disable-next-line
	}, [props.rows, props.selectFirstRow]);

	useEffect(() => {
		if (props.autoReselect?.reselect && props.controlledSelections) {
			cambiaSelezione({
				type: "include",
				ids: new Set(props.controlledSelections.map((selection) => selection.id)),
			});
		}
		// eslint-disable-next-line
	}, [props.controlledSelections]);

	useEffect(() => {
		if (props.page !== paginationModel.page) {
			setPaginationModel({
				pageSize: props.pageSize,
				page: props.paginationMode !== "client" ? props.page : 0,
			});
		}
	}, [props.page]);

	useEffect(() => {
		localStorage.setItem(settingsStorageKey, JSON.stringify(settings));
	}, [settings]);

	// Il riordino delle colonne via drag&drop è una feature Pro: la versione Community forza
	// "disableColumnReorder" a true (non sovrascrivibile via props), quindi gli header non diventano mai
	// draggable e non emettono i relativi eventi. La implementiamo quindi manualmente attaccando dei
	// listener nativi di drag&drop direttamente sugli elementi DOM degli header.
	useEffect(() => {
		const gridNode = gridRef.current;
		if (!gridNode) {
			return undefined;
		}

		const cleanupFns = [];
		const headerCells = gridNode.querySelectorAll(".MuiDataGrid-columnHeader[data-field]");

		headerCells.forEach((headerCell) => {
			const field = headerCell.getAttribute("data-field");
			const col = columnsGrid.find((c) => c.field === field);
			if (!col || col.disableReorder) {
				return;
			}

			const draggableContainer = headerCell.querySelector(".MuiDataGrid-columnHeaderDraggableContainer");
			if (!draggableContainer) {
				return;
			}

			const handleDragStart = (event) => {
				draggedColumnField.current = field;
				dragOverColumnField.current = field;
				if (event.dataTransfer) {
					event.dataTransfer.effectAllowed = "move";
					try {
						event.dataTransfer.setData("text/plain", field);
					} catch (error) {
						// alcuni browser richiedono setData per avviare il drag, ignoro eventuali errori
					}
				}
			};

			const handleDragEnter = (event) => {
				event.preventDefault();
				if (draggedColumnField.current && field !== draggedColumnField.current) {
					dragOverColumnField.current = field;
				}
			};

			const handleDragOver = (event) => {
				event.preventDefault();
			};

			const handleDragEnd = (event) => {
				event.preventDefault();

				const sourceField = draggedColumnField.current;
				const targetField = dragOverColumnField.current;
				draggedColumnField.current = null;
				dragOverColumnField.current = null;

				if (!sourceField || !targetField || sourceField === targetField) {
					return;
				}

				onColumnReorderCustom(sourceField, targetField);
			};

			draggableContainer.setAttribute("draggable", "true");
			draggableContainer.addEventListener("dragstart", handleDragStart);
			draggableContainer.addEventListener("dragenter", handleDragEnter);
			draggableContainer.addEventListener("dragover", handleDragOver);
			draggableContainer.addEventListener("dragend", handleDragEnd);

			cleanupFns.push(() => {
				draggableContainer.removeEventListener("dragstart", handleDragStart);
				draggableContainer.removeEventListener("dragenter", handleDragEnter);
				draggableContainer.removeEventListener("dragover", handleDragOver);
				draggableContainer.removeEventListener("dragend", handleDragEnd);
			});
		});

		return () => {
			cleanupFns.forEach((cleanup) => cleanup());
		};
		// eslint-disable-next-line
	}, [columnsGrid, columnVisibilityModel, visualizeGrid]);

	// Sposta le colonne startPinned in testa e le endPinned in coda, mantenendo l'ordine relativo tra loro
	const reorderPinnedColumns = (cols) => {
		const start = cols.filter((c) => c.startPinned);
		const end = cols.filter((c) => c.endPinned && !c.startPinned);
		const middle = cols.filter((c) => !c.startPinned && !c.endPinned);
		return [...start, ...middle, ...end];
	};

	const manageColumn = (column) => {
		if (column.valueFormatter || column.actionColumn) {
			return column;
		} else {
			// Metto il tooltip ai dati che vengono troncati perchè troppo lunghi
			let renderCell = column.renderCell;
			if (!renderCell) {
				renderCell = (params) => {
					if (column.tooltip) {
						return (
							<Tooltip
								title={
									<Typography sx={{ whiteSpace: "pre-line" }} fontSize={14}>
										{formatLabel(column.tooltip, params.row)}
									</Typography>
								}
							>
								<span
									style={{
										overflow: "hidden",
										textOverflow: "ellipsis",
									}}
								>
									{params.value}
								</span>
							</Tooltip>
						);
					} else {
						const charPixels = 7.6;
						const columnWidth = params.colDef.computedWidth;
						if (params.value) {
							const stringLength = params.value.length;
							const isStringCut = columnWidth < stringLength * charPixels;
							if (isStringCut) {
								return (
									<Tooltip title={<Typography fontSize={14}>{params.value}</Typography>}>
										<span
											style={{
												overflow: "hidden",
												textOverflow: "ellipsis",
											}}
										>
											{params.value}
										</span>
									</Tooltip>
								);
							} else {
								return <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{params.value}</span>;
							}
						}
					}
				};
			}
			return {
				...column,
				renderCell: renderCell,
			};
		}
	};

	const getSavedColumns = () => {
		const savedColumnsString = localStorage.getItem(gridId);
		let savedColumns;
		if (savedColumnsString && savedColumnsString !== "undefined" && !window["Configs"].configTest && !props.disableSaveColumns) {
			try {
				savedColumns = JSON.parse(savedColumnsString);
			} catch (error) {
				savedColumns = null;
			}
		}

		if (!savedColumns || savedColumns.length === 0) {
			const columnsToSave = props.columns?.map((column) => {
				const objMod = objModArray.find((objModObject) => objModObject.moduleName === componentModule && objModObject.idObject === column.objmod);

				let columnToSave = {
					field: column.field,
					width: column.width,
					hidden: !isComponentVisible(objMod, column.hidden),
					startPinned: column.startPinned,
					endPinned: column.endPinned,
				};
				return columnToSave;
			});

			if (!window["Configs"].configTest && !props.disableSaveColumns) {
				localStorage.setItem(gridId, JSON.stringify(columnsToSave));
			}
			savedColumns = columnsToSave;
		}

		return savedColumns;
	};

	const cambiaSelezione = (newSelectionModel) => {
		//Cerco gli ID di newSelectionModel nelle righe perchè se viene rieseguita la find cambiano gli ID delle righe
		let selectionArray = [...newSelectionModel.ids];
		if (props.selection?.multipleSelection) {
			let multipleElementArray = [];
			let multipleElementArrayId = [];
			if (newSelectionModel.type === "exclude") {
				//selezionate tutte le celle
				props.rows.forEach((row) => {
					multipleElementArray.push(row);
					multipleElementArrayId.push(row.id);
				});
			} else {
				selectionArray.forEach((selectedId) => {
					let rigaSelezionata = props.rows.find((row) => row.id === selectedId);
					if (rigaSelezionata) {
						multipleElementArray.push(rigaSelezionata);
						multipleElementArrayId.push(rigaSelezionata.id);
					}
				});
			}
			if (props.selection?.selectionFunction) {
				props.selection.selectionFunction(multipleElementArray);
			}
			setSelectionModel({
				type: "include",
				ids: new Set(multipleElementArrayId),
			});
		} else {
			const rigaSelezionata = props.rows.find((row) => row.id === selectionArray[newSelectionModel.ids.size - 1]);

			let singleElementArrayId = [];
			if (rigaSelezionata) {
				singleElementArrayId.push(rigaSelezionata.id);
			}

			if (props.selection?.selectionFunction) {
				props.selection.selectionFunction(rigaSelezionata);
			}

			if (props.setSelectedRecord) {
				props.setSelectedRecord(rigaSelezionata);
			}
			setSelectionModel({
				type: "include",
				ids: new Set(singleElementArrayId),
			});
		}
	};

	function onFilterChange(newFilters) {
		let newGridFilters = newFilters.items;
		let mergedFilters = [...props.gridFilters];

		newGridFilters.forEach((newGridFilter) => {
			const existingFilterIndex = mergedFilters.findIndex((filter) => filter.id === newGridFilter.id);

			if (existingFilterIndex !== -1) {
				if (newGridFilter.value || newGridFilter.value === 0 || newGridFilter.operator === "isEmpty" || newGridFilter.operator === "isNotEmpty") {
					// Aggiorno il filtro
					mergedFilters[existingFilterIndex] = newGridFilter;
				} else {
					// Rimuovi il filtro
					mergedFilters = mergedFilters.filter((filter) => filter.id !== newGridFilter.id);
				}
			} else {
				// Aggiungo il filtro
				if (newGridFilter.value || newGridFilter.value === 0 || newGridFilter.operator === "isEmpty" || newGridFilter.operator === "isNotEmpty") {
					mergedFilters.push(newGridFilter);
				}
			}
		});

		if (props.filterMode === "client") {
			let filteredRows = props.originalRows;

			if (mergedFilters.length > 0) {
				filteredRows = props.originalRows.filter((row) => {
					let isRowValid = true;
					for (const filter of mergedFilters) {
						if (!applyClientFilter(row[filter.field], filter.operator, filter.value)) {
							isRowValid = false;
							break;
						}
					}
					return isRowValid;
				});
			}

			props.setRows(filteredRows);
		}

		props.changeGridFilters?.(mergedFilters);
	}

	function applyClientFilter(field, operator, value) {
		switch (operator) {
			case "contains":
				return field?.toLowerCase().includes(value?.toLowerCase());
			case "doesNotContain":
				return !applyClientFilter(field, "contains", value);
			case "equals":
				return field?.toLowerCase() === value?.toLowerCase();
			case "startsWith":
				return field?.toLowerCase().startsWith(value?.toLowerCase());
			case "endsWith":
				return field?.toLowerCase().endsWith(value?.toLowerCase());
			case "isEmpty":
				return field?.toLowerCase() === "" || field == null;
			case "isNotEmpty":
				return !applyClientFilter(field, "isEmpty", value);
			default:
				return true;
		}
	}

	function onChangeVisibilityCustom(newModel) {
		if (props.gridId || props.findFunction) {
			const savedColumns = getSavedColumns();

			if (Object.keys(newModel).length === 0) {
				//tutte le colonne visibili
				savedColumns.map((savedColumn) => (savedColumn.hidden = false));
			} else {
				for (const column in newModel) {
					if (newModel.hasOwnProperty(column)) {
						const hidden = !newModel[column];

						const matchingSavedColumn = savedColumns.find((savedColumn) => savedColumn.field === column);

						if (matchingSavedColumn) {
							matchingSavedColumn.hidden = hidden;
						} else {
							const matchingColumnGrid = columnsGrid.find((columnGrid) => columnGrid.field === column);

							const columnToAdd = {
								field: column,
								width: 120,
								hidden: hidden,
								startPinned: matchingColumnGrid?.startPinned,
								endPinned: matchingColumnGrid?.endPinned,
							};

							savedColumns.push(columnToAdd);
						}
					}
				}
			}

			if (!window["Configs"].configTest && !props.disableSaveColumns) {
				localStorage.setItem(gridId, JSON.stringify(savedColumns));
			}
		}

		setColumnVisibilityModel(newModel);
	}

	function onColumnResizeCustom(column) {
		if (props.gridId || props.findFunction) {
			const savedColumns = getSavedColumns();

			let matchingSavedColumn = savedColumns.find((savedColumn) => savedColumn.field === column.colDef.field);

			if (matchingSavedColumn) {
				matchingSavedColumn.width = column.width;
			} else {
				const columnToAdd = {
					field: column.colDef.field,
					width: column.width,
					hidden: false,
					startPinned: column.colDef.startPinned,
					endPinned: column.colDef.endPinned,
				};

				savedColumns.push(columnToAdd);
			}
			if (!window["Configs"].configTest && !props.disableSaveColumns) {
				localStorage.setItem(gridId, JSON.stringify(savedColumns));
			}
		}

		let newColumnsGrid = columnsGrid.map((newColumnGrid) => {
			if (newColumnGrid.field === column.colDef.field) {
				return { ...newColumnGrid, width: column.width, flex: undefined };
			}
			return newColumnGrid;
		});
		setColumnsGrid(newColumnsGrid);
	}

	// Riordina le colonne via drag&drop (feature non disponibile nella versione Community di SaitGrid) e ne salva la posizione
	function onColumnReorderCustom(sourceField, targetField) {
		const sourceIndex = columnsGrid.findIndex((col) => col.field === sourceField);
		const targetIndex = columnsGrid.findIndex((col) => col.field === targetField);

		if (sourceIndex === -1 || targetIndex === -1 || sourceIndex === targetIndex) {
			return;
		}

		const newColumnsGrid = [...columnsGrid];
		const [movedColumn] = newColumnsGrid.splice(sourceIndex, 1);
		newColumnsGrid.splice(targetIndex, 0, movedColumn);

		const reordered = reorderPinnedColumns(newColumnsGrid);

		if (props.gridId || props.findFunction) {
			const savedColumns = getSavedColumns();

			reordered.forEach((col, index) => {
				const matchingSavedColumn = savedColumns.find((savedColumn) => savedColumn.field === col.field);

				if (matchingSavedColumn) {
					matchingSavedColumn.order = index;
				} else {
					savedColumns.push({
						field: col.field,
						width: col.width,
						hidden: col.hidden,
						startPinned: col.startPinned,
						endPinned: col.endPinned,
						order: index,
					});
				}
			});

			if (!window["Configs"].configTest && !props.disableSaveColumns) {
				localStorage.setItem(gridId, JSON.stringify(savedColumns));
			}
		}

		setColumnsGrid(reordered);
	}

	function togglePinColumn(field, side) {
		const savedColumns = props.gridId || props.findFunction ? getSavedColumns() : null;

		const newColumnsGrid = columnsGrid.map((col) => {
			if (col.field !== field) {
				return col;
			}

			const currentlyPinned = side === "start" ? col.startPinned : col.endPinned;
			const updatedCol = {
				...col,
				startPinned: side === "start" ? !currentlyPinned : false,
				endPinned: side === "end" ? !currentlyPinned : false,
			};

			// Se la colonna è pinnata, non può avere flex (altrimenti occupa lo spazio residuo e sfalsa le coordinate sticky)
			if ((updatedCol.startPinned || updatedCol.endPinned) && updatedCol.flex && !updatedCol.width) {
				updatedCol.width = updatedCol.minWidth || 150;
				updatedCol.flex = undefined;
			}

			return updatedCol;
		});

		const reordered = reorderPinnedColumns(newColumnsGrid);

		if (savedColumns) {
			const updatedCol = reordered.find((col) => col.field === field);
			let matchingSavedColumn = savedColumns.find((savedColumn) => savedColumn.field === field);

			if (matchingSavedColumn) {
				matchingSavedColumn.startPinned = updatedCol.startPinned;
				matchingSavedColumn.endPinned = updatedCol.endPinned;
			} else {
				savedColumns.push({
					field,
					width: updatedCol.width,
					hidden: updatedCol.hidden,
					startPinned: updatedCol.startPinned,
					endPinned: updatedCol.endPinned,
				});
			}

			if (!window["Configs"].configTest && !props.disableSaveColumns) {
				localStorage.setItem(gridId, JSON.stringify(savedColumns));
			}
		}

		setColumnsGrid(reordered);
	}

	function onPageChange(newPage) {
		if (props.paginationMode === "client" || props.page !== newPage.page) {
			setPaginationModel({
				pageSize: props.pageSize,
				page: newPage.page,
			});
			if (props.paginationMode !== "client") {
				props.handlePageChanged(newPage.page);
			}
		}
	}

	// PINNED COLUMNS STYLES (Start & End pinned support for MUI DataGrid Community)
	const pinnedSx = React.useMemo(() => {
		if (!columnsGrid || columnsGrid.length === 0) {
			return {};
		}

		const isColVisible = (col) => {
			if (columnVisibilityModel && columnVisibilityModel[col.field] !== undefined) {
				return columnVisibilityModel[col.field];
			}
			return !col.hidden;
		};

		// 1. Start pinned columns (consecutive from index 0)
		const startPinnedCols = [];
		for (let i = 0; i < columnsGrid.length; i++) {
			const col = columnsGrid[i];
			if (!isColVisible(col)) continue;
			if (col.startPinned) {
				startPinnedCols.push(col);
			} else {
				break; // Ignore any subsequent startPinned in the middle
			}
		}

		// 2. End pinned columns (consecutive from the last index backwards)
		const endPinnedCols = [];
		for (let i = columnsGrid.length - 1; i >= 0; i--) {
			const col = columnsGrid[i];
			if (!isColVisible(col)) continue;
			if (col.endPinned) {
				if (!startPinnedCols.some((c) => c.field === col.field)) {
					endPinnedCols.push(col);
				}
			} else {
				break; // Ignore any prior endPinned in the middle
			}
		}

		if (startPinnedCols.length === 0 && endPinnedCols.length === 0) {
			return {};
		}

		const styles = {};
		const hasCheckbox = Boolean(props.selection?.checkboxSelection || props.selection?.multipleSelection);
		const checkboxWidth = 50;
		const dividerColor = theme.palette.divider;
		const headerBg = theme.palette.primary.light;

		let currentLeft = 0;
		if (startPinnedCols.length > 0 && hasCheckbox) {
			styles["& .MuiDataGrid-columnHeader[data-field='__check__']"] = {
				position: "sticky !important",
				left: 0,
				zIndex: "110 !important",
				backgroundColor: `${headerBg} !important`,
			};
			styles["& .MuiDataGrid-cell[data-field='__check__']"] = {
				position: "sticky !important",
				left: 0,
				zIndex: "3 !important",
				backgroundColor: "inherit",
			};
			currentLeft += checkboxWidth;
		}

		startPinnedCols.forEach((col, index) => {
			const colWidth = col.width || col.minWidth || 100;
			const isLastStart = index === startPinnedCols.length - 1;

			styles[`& .MuiDataGrid-columnHeader[data-field='${col.field}']`] = {
				position: "sticky !important",
				left: `${currentLeft}px`,
				zIndex: "100 !important",
				backgroundColor: `${headerBg} !important`,
				...(isLastStart && {
					borderRight: `1px solid ${dividerColor} !important`,
				}),
			};

			styles[`& .MuiDataGrid-cell[data-field='${col.field}']`] = {
				position: "sticky !important",
				left: `${currentLeft}px`,
				zIndex: "2 !important",
				backgroundColor: "inherit",
				...(isLastStart && {
					borderRight: `1px solid ${dividerColor} !important`,
				}),
			};

			currentLeft += colWidth;
		});

		// currentRight=1 prevents the sub-pixel jitter when scrolling to the end.
		// The ::after pseudo-element covers the resulting 1px gap:
		//   - header uses the known headerBg color
		//   - cell uses 'inherit' so it picks up whatever the row background is
		//     (alternating rows, selected, hover, colored rows, etc.)
		let currentRight = 1;
		endPinnedCols.forEach((col, index) => {
			const colWidth = col.width || col.minWidth || 100;
			const isFirstEnd = index === endPinnedCols.length - 1; // leftmost among end-pinned
			const rightOffset = `calc(${currentRight}px + (var(--DataGrid-hasScrollY, 0) * var(--DataGrid-scrollbarSize, 0px)))`;

			styles[`& .MuiDataGrid-columnHeader[data-field='${col.field}']`] = {
				position: "sticky !important",
				right: rightOffset,
				zIndex: "100 !important",
				backgroundColor: `${headerBg} !important`,
				overflow: "visible !important",
				"&::after": {
					content: '""',
					position: "absolute",
					top: 0,
					right: "-1px",
					bottom: 0,
					width: "1px",
					backgroundColor: headerBg,
					zIndex: 1,
				},
				...(isFirstEnd && {
					borderLeft: `1px solid ${dividerColor} !important`,
				}),
			};

			styles[`& .MuiDataGrid-cell[data-field='${col.field}']`] = {
				position: "sticky !important",
				right: rightOffset,
				zIndex: "2 !important",
				backgroundColor: "inherit",
				overflow: "visible !important",
				"&::after": {
					content: '""',
					position: "absolute",
					top: 0,
					right: "-1px",
					bottom: 0,
					width: "1px",
					backgroundColor: "inherit",
					zIndex: 1,
				},
				...(isFirstEnd && {
					borderLeft: `1px solid ${dividerColor} !important`,
				}),
			};

			currentRight += colWidth;
		});

		if (endPinnedCols.length > 0) {
			styles["& .MuiDataGrid-scrollbarFiller--header"] = {
				position: "sticky !important",
				right: "0 !important",
				zIndex: "100 !important",
				backgroundColor: `${headerBg} !important`,
				minWidth: "calc(var(--DataGrid-hasScrollY, 0) * var(--DataGrid-scrollbarSize, 0px)) !important",
				width: "calc(var(--DataGrid-hasScrollY, 0) * var(--DataGrid-scrollbarSize, 0px)) !important",
			};
			styles["& .MuiDataGrid-scrollbar--vertical"] = {
				backgroundColor: theme.palette.background.default,
			};
		}

		return styles;
	}, [columnsGrid, columnVisibilityModel, props.selection, theme]);

	const StyledToolbar = styled(Toolbar)`
		display: flex;
		justify-content: space-between;
		align-items: center;
		min-height: 37px;
	`;

	function CustomToolbar() {
		return (
			<StyledToolbar>
				{props.noStandardToolbar ? (
					<Box />
				) : (
					<Box
						sx={{
							...(props.toolbarIcons && {
								display: "flex",
								justifyContent: "space-evenly",
								alignItems: "center",
								width: "100%",
							}),
						}}
					>
						<GridToolbarReloadButton
							isFiltering={props.isFiltering}
							handleFireFilter={props.handleFireFilter}
							disableToolbarFilterButton={props.disableToolbarFilterButton}
							toolbarIcons={props.toolbarIcons}
						/>
						<GridToolbarColumnsButton toolbarIcons={props.toolbarIcons} />
						<GridToolbarDensitySelector settings={settings} onSettingsChange={setSettings} toolbarIcons={props.toolbarIcons} />
						<GridToolbarFiltersButton
							gridFilters={props.gridFilters}
							changeGridFilters={props.changeGridFilters}
							columnsGrid={columnsGrid}
							isFiltering={props.isFiltering}
							savedFilters={props.savedFilters}
							applySavedFilters={props.applySavedFilters}
							deleteSavedFilter={props.deleteSavedFilter}
							setNewReportName={props.setNewReportName}
							toolbarIcons={props.toolbarIcons}
						/>
						<GridToolbarExcelButton
							rowCount={props.rowCount}
							columns={props.columns}
							columnsVisibility={columnVisibilityModel}
							fetchDataForExcel={props.fetchDataForExcel}
							rowBackgroundColor={props.rowBackgroundColor}
							isFiltering={props.isFiltering}
							rows={props.rows}
							toolbarIcons={props.toolbarIcons}
						/>
						{props.pivotFunction && (
							<GridToolbarExcelPivotButton
								pivotFunction={props.pivotFunction}
								rowCount={props.rowCount}
								columns={props.columns}
								columnsVisibility={columnVisibilityModel}
								fetchDataForExcel={props.fetchDataForExcel}
								isFiltering={props.isFiltering}
								rows={props.rows}
								toolbarIcons={props.toolbarIcons}
							/>
						)}
						{!props.noAutoreload && (
							<GridToolbarAutoreloadButton
								isFiltering={props.isFiltering}
								setOpenDialogAutoreload={setOpenDialogAutoreload}
								autoreloadOn={autoreloadOn}
								toolbarIcons={props.toolbarIcons}
							/>
						)}
					</Box>
				)}
				<Box>
					{props.topButtons?.map((topButton, index) => {
						switch (topButton.type) {
							case BUTTON_UPLOAD:
								return (
									<SaitButtonUpload
										key={index}
										sx={{ ml: "5px" }}
										text={topButton.text}
										onUpload={topButton.onUpload}
										endIcon={topButton.icon}
										multiple={topButton.multiple}
										accept={topButton.accept}
										variant={topButton.variant || "text"}
										disabled={topButton.disabled || !checkIsRoleAllowed(topButton.roleSecurity, userPreferences)}
										objmod={topButton.objmod}
									/>
								);
							case BUTTON_LABEL:
								return (
									<Button
										key={index}
										variant="text"
										disableElevation
										disableFocusRipple
										disableRipple
										tabIndex={-1}
										sx={{
											textTransform: "none",
											color: "#000",
											pointerEvents: "none",
											"&:focus": {
												outline: "none",
											},
											"&.Mui-focusVisible": {
												boxShadow: "none",
												outline: "none",
											},
										}}
									>
										{topButton.text}
									</Button>
								);
							default:
								return (
									<SaitButton
										key={index}
										sx={{ ml: "5px" }}
										text={topButton.text}
										loading={props.isFiltering}
										onClick={topButton.function}
										endIcon={topButton.icon}
										color={topButton.color || "primary"}
										variant={topButton.variant || "text"}
										disabled={topButton.disabled || !checkIsRoleAllowed(topButton.roleSecurity, userPreferences)}
										objmod={topButton.objmod}
										hidden={topButton.hidden ? topButton.hidden : false}
									/>
								);
						}
					})}
				</Box>
			</StyledToolbar>
		);
	}

	const CustomFilterMenu = (props) => {
		const { colDef } = props;

		return <div style={{ zIndex: 99999 }}>{colDef.headerName}</div>;
	};

	const PinningMenuItem = (menuItemProps) => {
		const { colDef, onClick } = menuItemProps;

		const handlePin = (side) => (event) => {
			togglePinColumn(colDef.field, side);
			onClick(event);
		};

		return (
			<>
				<MenuItem onClick={handlePin("start")}>
					<ListItemIcon>
						<PushPinIcon fontSize="small" color={colDef.startPinned ? "primary" : "inherit"} />
					</ListItemIcon>
					<ListItemText>{colDef.startPinned ? i18next.t("unpin_column", { ns: saitfecoreNs }) : i18next.t("pin_start_column", { ns: saitfecoreNs })}</ListItemText>
				</MenuItem>
				<MenuItem onClick={handlePin("end")}>
					<ListItemIcon>
						<PushPinIcon fontSize="small" color={colDef.endPinned ? "primary" : "inherit"} sx={{ transform: "scaleX(-1)" }} />
					</ListItemIcon>
					<ListItemText>{colDef.endPinned ? i18next.t("unpin_column", { ns: saitfecoreNs }) : i18next.t("pin_end_column", { ns: saitfecoreNs })}</ListItemText>
				</MenuItem>
			</>
		);
	};

	const CustomColumnMenu = (menuProps) => {
		return (
			<GridColumnMenu
				{...menuProps}
				slots={{
					columnMenuPinningItem: PinningMenuItem,
				}}
				slotProps={{
					columnMenuPinningItem: {
						displayOrder: 15,
					},
				}}
			/>
		);
	};

	const CustomFilterPanel = (props) => {
		const apiContext = useGridApiContext();

		const handleKeyDown = (event) => {
			if (event.key === "Enter") {
				// Chiudi il pannello dei filtri
				apiContext.current.hideFilterPanel();
			}
		};

		return (
			// @ts-ignore
			<div onKeyDown={handleKeyDown}>
				<GridFilterPanel {...props} />
			</div>
		);
	};

	function CustomPagination() {
		const totalPages = props.paginationMode !== "client" ? Math.ceil((props.rowCount || 0) / paginationModel.pageSize) : Math.ceil((props.rows?.length || 0) / paginationModel.pageSize);

		const currentPage = paginationModel.page + 1; // MUI usa 0-based
		const pageSize = paginationModel.pageSize;
		const totalRecords = props.paginationMode !== "client" ? props.rowCount || 0 : props.rows?.length || 0;

		const from = totalRecords === 0 ? 0 : paginationModel.page * pageSize + 1;
		const to = Math.min(currentPage * pageSize, totalRecords);

		const [inputValue, setInputValue] = React.useState(String(currentPage));

		React.useEffect(() => {
			setInputValue(String(currentPage));
		}, [currentPage]);

		const goToPage = (page) => {
			const clamped = Math.max(1, Math.min(totalPages, page));
			onPageChange({ page: clamped - 1 }); // onPageChange già definita in SaitGrid
		};

		const handleInputCommit = () => {
			const v = parseInt(inputValue);
			if (!isNaN(v)) goToPage(v);
			else setInputValue(String(currentPage));
		};

		return (
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					px: 1.5,
					py: 0,
					minHeight: "30px",
					width: "100%",
				}}
			>
				{/* Record info */}
				<Typography sx={{ color: "text.secondary", font: "400 0.75rem / 1.43 tahoma" }}>{totalRecords === 0 ? "Nessun record" : `Record ${from}–${to} di ${totalRecords}`}</Typography>
				{/* Paginazione */}
				<Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
					{/* Prima pagina */}
					<IconButton size="small" disabled={currentPage <= 1 || props.isFiltering} onClick={() => goToPage(1)}>
						<KeyboardDoubleArrowLeftIcon />
					</IconButton>
					{/* Pagina precedente */}
					<IconButton
						size="small"
						disabled={currentPage <= 1 || props.isFiltering}
						onClick={() => {
							goToPage(currentPage - 1);
						}}
					>
						<KeyboardArrowLeftIcon />
					</IconButton>

					{/* Input pagina */}
					<Box sx={{ display: "flex", alignItems: "center", gap: "6px", fontSize: 13, color: "text.secondary" }}>
						<Typography variant="caption" sx={{ whiteSpace: "nowrap", fontSize: 13, pl: 1 }}>
							Pagina
						</Typography>
						<input
							value={inputValue}
							onChange={(e) => setInputValue(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") handleInputCommit();
							}}
							disabled={props.isFiltering}
							onBlur={handleInputCommit}
							style={{
								width: 44,
								height: 24,
								textAlign: "center",
								fontSize: 13,
								border: "0.5px solid #999999",
								borderRadius: 5,
								outline: "none",
								paddingTop: "2px",
								backgroundColor: theme.palette.background.paper,
								color: theme.palette.text.primary,
							}}
							type="number"
							min={1}
							max={totalPages}
						/>
						<Typography variant="caption" sx={{ whiteSpace: "nowrap", fontSize: 13, pr: 1 }}>
							di {totalPages}
						</Typography>
					</Box>

					{/* Pagina successiva */}
					<IconButton size="small" disabled={currentPage >= totalPages || props.isFiltering} onClick={() => goToPage(currentPage + 1)}>
						<KeyboardArrowRightIcon />
					</IconButton>
					{/* Ultima pagina */}
					<IconButton size="small" disabled={currentPage >= totalPages || props.isFiltering} onClick={() => goToPage(totalPages)}>
						<KeyboardDoubleArrowRightIcon />
					</IconButton>
				</Box>
			</Box>
		);
	}

	return (
		<>
			{visualizeGrid && (
				<Box
					sx={{
						minHeight: props.height ? props.height : props.minHeight || defaultMinHeight,
						flex: 1,
						position: "relative",
						...(props.disabled && {
							pointerEvents: "none",
							opacity: 0.6,
						}),
					}}
				>
					<Box ref={gridRef} sx={{ position: "absolute", inset: 0 }}>
						<DataGrid
							slots={{
								toolbar: CustomToolbar,
								filterMenu: CustomFilterMenu,
								filterPanel: CustomFilterPanel,
								pagination: CustomPagination,
								columnMenu: CustomColumnMenu,
								...props.slots,
							}}
							sx={{
								boxShadow: 1,
								border: 1,
								borderColor: "primary.light",
								"& .MuiDataGrid-columnHeader": {
									backgroundColor: "primary.light",
								},
								"& .MuiDataGrid-row": {
									backgroundColor: theme.palette.background.paper,
								},
								"& .MuiDataGrid-row:nth-of-type(even)": {
									backgroundColor: theme.palette.background.default,
								},
								"& .MuiDataGrid-row:nth-of-type(odd)": {
									backgroundColor: theme.palette.background.paper,
								},
								"& .MuiDataGrid-row.Mui-selected": {
									color: "primary.dark",
									backgroundColor: "primary.light",
								},
								"& .MuiDataGrid-row:hover": {
									backgroundColor: theme.palette.mode === "dark"
										? lighten(theme.palette.background.paper, 0.12)
										: darken(theme.palette.background.paper, 0.05),
								},
								"& .MuiDataGrid-row.Mui-selected:hover": {
									backgroundColor: "primary.light",
								},
								"& .MuiDataGrid-footerContainer": {
									height: "30px",
									minHeight: "30px",
									flexWrap: "nowrap",
									overflow: "hidden",
								},
								"& .MuiDataGrid-selectedRowCount": {
									whiteSpace: "nowrap",
									fontSize: 12,
									minWidth: "fit-content",
								},
								"& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
									outline: "none",
								},
								"& .MuiTablePagination-root": {
									maxHeight: "unset !important",
								},
								"& .MuiDataGrid-columnHeaderDraggableContainer": {
									cursor: "grab",
								},

								// Genera stili per tutte le righe colorate
								...["green", "red", "yellow", "grey"].reduce(
									(acc, color) => ({
										...acc,
										[`& .MuiDataGrid-row.${color}-row`]: {
											backgroundColor: theme.palette[`${color}Cell`].main,
										},
										[`& .MuiDataGrid-row.${color}-row:hover`]: {
											backgroundColor: alpha(theme.palette[`${color}Cell`].main, 0.7),
										},
										[`& .MuiDataGrid-row.${color}-row.Mui-selected`]: {
											backgroundColor: alpha(theme.palette[`${color}Cell`].main, 0.5),
											color: "primary.dark",
										},
										[`& .MuiDataGrid-row.${color}-row.Mui-selected:hover`]: {
											backgroundColor: alpha(theme.palette[`${color}Cell`].main, 0.6),
										},
									}),
									{},
								),
								// Genera stili per solo bordo superiore
								...["green", "red", "yellow", "grey"].reduce(
									(acc, color) => ({
										...acc,
										[`& .MuiDataGrid-row.${color}-border-top`]: {
											borderTop: `3px solid ${theme.palette[`${color}Cell`].main}`,
										},
									}),
									{},
								),
								minHeight: props.height ? props.height : props.minHeight || defaultMinHeight,
								...pinnedSx,
								...props.sx,
							}}
							disableVirtualization={props.disableVirtualization !== undefined ? props.disableVirtualization : true}
							slotProps={{
								panel: {
									// @ts-ignore
									placement: "bottom-start", // Posizione del pannello colonne/filtri toolbar
									...props.slotProps?.panel,
								},
								basePopper: {
									placement: "bottom-start", // Fa aprire il menu dei 3 puntini verso destra
									...props.slotProps?.basePopper,
								},
								...props.slotProps,
							}}
							getRowClassName={(params) => {
								if (props.rowBackgroundColor) {
									return props.rowBackgroundColor(params.row) || "";
								}
							}}
							rows={props.rows}
							columns={columnsGrid.map((col) => ({
								...col,
								type: col.type || "string",
								...(col.width ? { flex: undefined } : {}),
							}))}
							paginationModel={paginationModel}
							onPaginationModelChange={onPageChange}
							pageSizeOptions={[props.pageSize]}
							checkboxSelection={props.selection?.checkboxSelection || props.selection?.multipleSelection}
							loading={props.isFiltering}
							paginationMode={props.paginationMode || "server"}
							rowCount={props.paginationMode !== "client" ? props.rowCount : undefined}
							sortingMode={props.sortingMode || "server"}
							onSortModelChange={props.sortingMode !== "client" ? props.handleSortChanged : undefined}
							rowHeight={props.rowHeight || 26}
							columnHeaderHeight={props.headerHeight || 32}
							hideFooterSelectedRowCount={!props.selection?.multipleSelection}
							hideFooterPagination={props.isFiltering}
							onRowSelectionModelChange={(newSelectionModel, details) => {
								cambiaSelezione(newSelectionModel);
							}}
							// @ts-ignore rowSelectionModel da errore sui tipi ma funziona
							rowSelectionModel={selectionModel}
							localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
							onRowDoubleClick={props.handleRowDoubleClick}
							hideFooter={props.hideFooter}
							isRowSelectable={props.isRowSelectable}
							columnVisibilityModel={columnVisibilityModel}
							onColumnVisibilityModelChange={(newModel) => onChangeVisibilityCustom(newModel)}
							onColumnResize={(column) => onColumnResizeCustom(column)}
							filterMode={props.filterMode || "server"}
							onFilterModelChange={onFilterChange}
							filterDebounceMs={300}
							onCellClick={(column, event) => {
								if (column.colDef?.["actionColumn"]) {
									event.stopPropagation();
									if (!props.isRowSelectable || props.isRowSelectable({ row: column.row })) {
										cambiaSelezione({
											type: "include",
											ids: new Set([column.row.id]),
										});
									}
								} else if (column.colDef?.["columnType"] === "notes") {
									event.stopPropagation();
								} else {
									column.colDef?.["onCellClick"]?.(column, event);
								}
							}}
							processRowUpdate={props.afterEdit}
							showToolbar={!props.noStandardToolbar || props.topButtons?.length > 0}
							density={settings.density}
							showCellVerticalBorder={settings.showCellBorders}
							showColumnVerticalBorder={settings.showColumnBorders}
							isCellEditable={props.isCellEditable}
						/>
					</Box>
				</Box>
			)}
			<GridToolbarAutoreloadDialog
				openDialogAutoreload={openDialogAutoreload}
				setOpenDialogAutoreload={setOpenDialogAutoreload}
				minAutoreloadSeconds={props.minAutoreloadSeconds}
				autoreloadOn={autoreloadOn}
				setAutoreloadOn={setAutoreloadOn}
				handleFireFilter={props.handleFireFilter}
				gridRef={gridRef}
			/>
		</>
	);
}
